// src/components/Footer.jsx
import React from "react";
import { Facebook, X, Instagram, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import googlePlayBadge from "../../icons/Google Play.svg";
import appStoreBadge from "../../icons/App store.svg";
import EmailSubscriptionManager from "../../services/home/EmailSubscriptionManager";
import { useState } from "react";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const handleNewsletterSubscribe = async () => {
    if (!email || !email.includes("@")) {
      setMessage("Please enter a valid email.");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        emailId: email,
        status: "ACTIVE",
      };

      const res = await EmailSubscriptionManager.createSubscription(payload);

      if (res.responseCode === 200) {
        setMessage("Thank you for subscribing!");
        setEmail("");
      } else {
        setMessage(res.responseMessage || "Subscription failed.");
      }
    } catch (err) {
      if (err.response) {
        const { responseCode, responseMessage } = err.response.data;

        if (responseCode === 409) {
          setMessage(responseMessage || "You're already subscribed.");
        } else {
          setMessage(responseMessage || "An error occurred. Please try again.");
        }
      } else {
        setMessage("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="w-full text-text-muted text-sm">
      {/* Top section: Newsletter signup & Social media */}
      <div className="bg-primary-shade1 w-full py-6 border-t border-b border-primary-shade3">
        <div className="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row justify-between gap-6">
          {/* Left Column: Info + Newsletter */}
          <div className="w-full lg:w-2/3 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Stay In The Know */}
            <div className="mb-1 md:mb-0 md:mr-6 text-center md:text-left">
              <h2 className="text-lg md:text-xl font-bold text-text-muted w-fit border-b-2 border-primary pb-1 mb-2">
                Stay In The Know
              </h2>
              <p className="text-sm">
                Subscribe to our newsletter and stay updated on latest offers,
                discounts and events near you.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email here"
                className="border border-primary-shade3 rounded px-3 py-2 outline-none text-sm w-full sm:w-64"
              />
              <button
                className="bg-primary text-white px-4 py-2 text-sm rounded font-semibold hover:bg-primary-shade5 w-full sm:w-auto"
                onClick={handleNewsletterSubscribe}
                disabled={submitting}
              >
                <span className="sm:hidden">Subscribe</span>
                <span className="hidden sm:inline-block">&#8594;</span>
              </button>
              {message && (
                <p className="text-xs text-red-600 sm:ml-2 mt-1 sm:mt-0">
                  {message}
                </p>
              )}
            </div>
          </div>

          {/* Right Column: Follow Us */}
          <div className="w-full lg:w-1/3 flex items-center justify-start lg:justify-end gap-4">
            <span className="text-lg md:text-xl font-bold text-text-muted">
              Follow us
            </span>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/bookatdoorsteps"
                aria-label="Facebook"
                className="bg-primary p-2 rounded-full text-white hover:bg-primary-shade5 transition"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://x.com/booksatdoorstep"
                aria-label="X (formerly Twitter)"
                className="bg-primary p-2 rounded-full text-white hover:bg-primary-shade5 transition"
              >
                <X className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/booksatdoorsteps/#"
                aria-label="Instagram"
                className="bg-primary p-2 rounded-full text-white hover:bg-primary-shade5 transition"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="#youtube"
                aria-label="YouTube"
                className="bg-primary p-2 rounded-full text-white hover:bg-primary-shade5 transition"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Middle section: 4-column footer links & contact */}
      <div className="bg-primary-shade1 py-8">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
          {/* Column 1: Customer Service */}
          <div>
            <h3 className="text-lg md:text-xl font-bold text-text-muted w-fit border-b-2 border-primary pb-1 mb-2">
              Customer Service
            </h3>

            <p className="text-sm leading-6 mb-2">
              Books At Doorstep, Office No. 1 First Floor, Chandrashila Complex,
              NDA Road,
              <br />
              Shivane Pune-411023
            </p>
            <p>
              <a
                href="mailto:care@booksatdoorsteps.com?subject=Support%20Request"
                className="text-primary text-base hover:text-primary-shade5 font-medium"
              >
                care@booksatdoorsteps.com
              </a>
            </p>

            <p className="text-base mt-3 font-bold flex flex-wrap items-center gap-1">
              <a
                href="tel:+919860133100"
                className="text-primary hover:text-primary-shade5"
              >
                +91 9860133100
              </a>
              <span className="text-gray-400 font-normal">/</span>
              <a
                href="tel:+918788734240"
                className="text-primary hover:text-primary-shade5"
              >
                +91 8788734240
              </a>
            </p>
          </div>

          {/* Column 2: Policies */}
          <div>
            <h3 className="text-lg md:text-xl font-bold text-text-muted w-fit border-b-2 border-primary pb-1 mb-2">
              Policies
            </h3>

            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/privacy-policy"
                  className="hover:text-primary-shade5"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms-and-conditions"
                  className="hover:text-primary-shade5"
                >
                  Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <Link
                  to="/cancellation-policy"
                  className="hover:text-primary-shade5"
                >
                  Cancellation Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/shipping-policy"
                  className="hover:text-primary-shade5"
                >
                  Shipping Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Help */}
          <div>
            <h3 className="text-lg md:text-xl font-bold text-text-muted w-fit border-b-2 border-primary pb-1 mb-2">
              Help
            </h3>
  {/* Investors column */}
{/*
    <ul className="space-y-2 text-sm">
      <li>
        <Link to="/investors" className="hover:text-primary-shade5">
          Investors
        </Link>
      </li>
    </ul> */}

            <ul className="space-y-2 text-sm">
            <li>
                <Link
                  to="/investors"
                  className="hover:text-primary-shade5"
                >
                  Investors
                </Link>
              </li>
              <li>
                <Link
                  to="/pickup-delivery-policy"
                  className="hover:text-primary-shade5"
                >
                  Pickup &amp; Delivery
                </Link>
              </li>
              <li>
                <Link to="/return-policy" className="hover:text-primary-shade5">
                  Return
                </Link>
              </li>
              <li>
                <Link to="/faq" className="hover:text-primary-shade5">
                  FAQ
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Get In Touch */}
          <div>
            <h3 className="text-lg md:text-xl font-bold text-text-muted w-fit border-b-2 border-primary pb-1 mb-2">
              Get In Touch
            </h3>

            <ul className="space-y-2 text-sm">
              <li>
                <a href="/contact-us" className="hover:text-primary-shade5">
                  Contact Us
                </a>
              </li>
            </ul>

            <div className="mt-4">
              <span className="block font-bold mb-2 text-center sm:text-left">
                Download Our App
              </span>

              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                <a
                  href="https://play.google.com/store/apps/details?id=com.applauseitsolutions.booksatdoorsteps"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={googlePlayBadge}
                    alt="Google Play"
                    className="h-7 object-contain"
                  />
                </a>
                  <img
                    src={appStoreBadge}
                    alt="App Store"
                    className="h-7 object-contain"
                  />
              </div>
            </div>
          </div>
        </div>
      </div>

<div className="bg-primary text-text-white py-3 text-center">
  <p className="text-sm">
    &copy; {new Date().getFullYear()} Booksatdoorsteps.com. All rights reserved | Designed and Developed by{" "}
    <a
      href="https://codevergeit.com/"
      className="hover:text-black/100 mx-1 underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      Codeverge IT Solutions
    </a>
    |
    <a
      href="/sitemap.xml"
      className="hover:text-black/100 mx-1"
      target="_blank"
      rel="noopener noreferrer"
    >
      Sitemap
    </a>
  </p>
</div>

    </footer>
  );
};

export default Footer;
